import React, { useState, useEffect } from "react";
import { XMarkIcon, CameraIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ImageWithFallback } from "../../../Utils/loader";
import { UrduInput } from "../../../Components/Shared/UrduInput";
import { ImageSeoFields } from "../../../Components/Shared/ImageSeoFields";

function AddContractCategoryModal({
  modalOpen,
  toggleModal,
  category,
  handleSaveCategory,
}) {
  const [categoryData, setCategoryData] = useState({
    title: "",
    categoryTitle_ur: "",
    description: "",
    description_ur: "",
    custom_url: "",
    image: null,
    image_alt: "",
    image_title: "",
  });

  const [titleError, setTitleError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [imageError, setImageError] = useState("");
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (category) {
      setCategoryData({
        title: category.title,
        categoryTitle_ur: category.categoryTitle_ur || "",
        description: category.description || "",
        description_ur: category.description_ur || "",
        custom_url: category.custom_url || "",
        image: category.image,
        image_alt: category.image_alt || "",
        image_title: category.image_title || "",
      });
      if (category.image) {
        setImagePreview(category.image);
      }
    } else {
      setCategoryData({
        title: "",
        categoryTitle_ur: "",
        description: "",
        description_ur: "",
        custom_url: "",
        image: null,
        image_alt: "",
        image_title: "",
      });
      setImagePreview(null);
    }
  }, [category]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCategoryData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (name === "description") {
      const wordCount = value.trim().split(/\s+/).filter(Boolean).length;
      if (wordCount > 100) {
        setDescriptionError("Description must not exceed 100 words.");
      } else {
        setDescriptionError("");
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const _URL = window.URL || window.webkitURL;
      const img = new Image();
      img.onload = () => {
        if (img.width === 450 && img.height === 450) {
          setCategoryData((prevData) => ({
            ...prevData,
            image: file,
          }));
          const reader = new FileReader();
          reader.onload = (e) => {
            setImagePreview(e.target.result);
          };
          reader.readAsDataURL(file);
          setImageError("");
        } else {
          setImageError("Image must be exactly 450x450 pixels.");
          setCategoryData((prevData) => ({
            ...prevData,
            image: null,
          }));
          setImagePreview(null);
          e.target.value = ""; 
        }
      };
      img.onerror = () => {
        setImageError("Invalid image file.");
      };
      img.src = _URL.createObjectURL(file);
    }
  };

  const handleRemoveImage = () => {
    setCategoryData((prevData) => ({
      ...prevData,
      image: null,
    }));
    setImagePreview(null);
    setImageError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    let formValid = true;

    setTitleError("");
    setDescriptionError("");
    setImageError("");
    const urlErrorField = document.getElementById("url-error");
    if (urlErrorField) urlErrorField.innerText = "";

    if (!categoryData.title) {
      setTitleError("Title is required.");
      formValid = false;
    }

    if (!categoryData.custom_url) {
        const field = document.getElementById("url-error");
        if (field) field.innerText = "Custom URL is required.";
        formValid = false;
    }

    if (!categoryData.description) {
        setDescriptionError("Description is required.");
        formValid = false;
    }

    const wordCount = categoryData.description.trim().split(/\s+/).filter(Boolean).length;
    if (wordCount > 100) {
      setDescriptionError("Description must not exceed 100 words.");
      formValid = false;
    }

    if (!categoryData.image) {
      setImageError("Image is required.");
      formValid = false;
    }

    if (!formValid) {
      return;
    }

    handleSaveCategory(categoryData, category ? category.id : null);
  };

  const handleCloseModal = () => {
    setCategoryData({
      title: "",
      categoryTitle_ur: "",
      description: "",
      description_ur: "",
      custom_url: "",
      image: null,
      image_alt: "",
      image_title: "",
    });
    setImagePreview(null);
    setTitleError("");
    setDescriptionError("");
    setImageError("");
    toggleModal();
  };

  const isFormValid = 
    categoryData.title.trim().length >= 2 && 
    categoryData.custom_url.trim().length >= 2 &&
    categoryData.description.trim().length >= 2 &&
    categoryData.image && 
    !descriptionError && 
    !imageError;

  return (
    modalOpen && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
        <div className="bg-white p-8 rounded-2xl w-[550px] max-h-[90vh] overflow-y-auto relative shadow-2xl">
          <XMarkIcon
            className="absolute top-4 right-4 h-6 w-6 text-gray-600 cursor-pointer hover:text-gray-800 transition-colors"
            onClick={handleCloseModal}
          />
          <h3 className="text-2xl font-semibold mb-6 text-center text-gray-800">
            {category ? "Edit Contract Category" : "Add Contract Category"}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Image Upload Section */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Category Image <span className="text-red-500">*</span>
              </label>

              {imagePreview ? (
                <div className="relative">
                  <div className="w-full h-48 bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200">
                    <ImageWithFallback
                      src={imagePreview}
                      alt="Category preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full transition-colors"
                    title="Remove image"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="image-upload-contract"
                  className="cursor-pointer w-full h-48 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:bg-gray-100 transition-colors"
                >
                  <CameraIcon className="h-12 w-12 text-gray-400 mb-3" />
                  <span className="text-gray-600 font-medium">Upload Image</span>
                  <span className="text-red-500 text-xs mt-1 font-bold">450x450 Required Only</span>
                </label>
              )}

              <input
                id="image-upload-contract"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />

              {imageError && (
                <p className="text-red-500 text-sm font-bold">{imageError}</p>
              )}
              <ImageSeoFields
                imageAlt={categoryData.image_alt}
                imageTitle={categoryData.image_title}
                onAltChange={(value) =>
                  setCategoryData((prev) => ({ ...prev, image_alt: value }))
                }
                onTitleChange={(value) =>
                  setCategoryData((prev) => ({ ...prev, image_title: value }))
                }
              />
            </div>

            {/* Title Input */}
            <div className="space-y-2">
              <label htmlFor="category-title" className="block text-sm font-medium text-gray-700">
                Category Title <span className="text-red-500">*</span>
              </label>
              <input
                id="category-title"
                type="text"
                name="title"
                value={categoryData.title}
                onChange={handleInputChange}
                placeholder="Enter category title"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                required
              />
              {titleError && (
                <p className="text-red-500 text-sm">{titleError}</p>
              )}
              <UrduInput
                label="زمرہ عنوان"
                name="categoryTitle_ur"
                value={categoryData.categoryTitle_ur}
                onChange={handleInputChange}
              />
            </div>

            {/* Custom URL Input */}
            <div className="space-y-2">
              <label htmlFor="category-url" className="block text-sm font-medium text-gray-700">
                Custom URL <span className="text-red-500">*</span>
              </label>
              <input
                id="category-url"
                type="text"
                name="custom_url"
                value={categoryData.custom_url}
                onChange={handleInputChange}
                placeholder="e.g. residential-contracts"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                required
              />
              <p id="url-error" className="text-red-500 text-sm"></p>
            </div>

            {/* Description Textarea */}
            <div className="space-y-2">
              <label htmlFor="category-description" className="block text-sm font-medium text-gray-700">
                Description <span className="text-red-500">*</span> <span className="text-gray-400 text-xs">(Max 100 words)</span>
              </label>
              <textarea
                id="category-description"
                name="description"
                value={categoryData.description}
                onChange={handleInputChange}
                placeholder="Enter category description..."
                rows="4"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                required
              ></textarea>
              <div className="flex justify-between items-center">
                {descriptionError ? (
                  <p className="text-red-500 text-sm">{descriptionError}</p>
                ) : (
                  <p className="text-gray-400 text-xs">
                    Words: {categoryData.description.trim().split(/\s+/).filter(Boolean).length}/100
                  </p>
                )}
              </div>
              <UrduInput
                label="تفصیل"
                name="description_ur"
                value={categoryData.description_ur}
                onChange={handleInputChange}
                type="textarea"
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={handleCloseModal}
                className="btn btn-reset-filter justify-center"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isFormValid}
                className={`btn justify-center ${
                  category ? "btn-edit text-white" : "btn-primary"
                } ${!isFormValid ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {category ? "Update" : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  );
}

export default AddContractCategoryModal;
